import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Info } from 'lucide-react';
import type { AddonSuggestion, AddonSuggestionType } from '@/types/addonSuggestions';

interface AddonSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: AddonSuggestion[];
  onAccept: (acceptedTypes: AddonSuggestionType[]) => void;
  onDismiss: () => void;
  /**
   * CORE-BP item 3 — the advisor left without using either button.
   *
   * Escape, the backdrop and the X are dismissals too, but they are NOT a
   * request to create the ticket, so they must not be folded into `onDismiss`
   * (which continues into OptiPlace). This reports the outcome and nothing
   * else. Optional so existing callers keep compiling; when it is absent the
   * showing simply goes unrecorded exactly as it did before.
   */
  onCloseWithoutChoice?: () => void;
  /**
   * CORE-BP item 6 — where this showing came from.
   *
   * 'create' is ticket creation, where accepting also creates the ticket.
   * 'existing-ticket' is a post-add showing: the ticket already exists and the
   * work that prompted the question is already on it, so promising to "create"
   * anything would be untrue. Only the WORDING differs; the question, the
   * outcome model and the human's authority are identical.
   */
  mode?: 'create' | 'existing-ticket';
  vehicleInfo: {
    year: number;
    make: string;
    model: string;
  };
  onSuppressType?: (type: AddonSuggestionType) => void;
}

export function AddonSuggestionModal({
  isOpen,
  onClose,
  suggestions,
  onAccept,
  onDismiss,
  onCloseWithoutChoice,
  mode = 'create',
  vehicleInfo,
  onSuppressType
}: AddonSuggestionModalProps) {
  // Initialize checked state based on defaultChecked from each suggestion
  const [checkedTypes, setCheckedTypes] = useState<Set<AddonSuggestionType>>(
    () => new Set(suggestions.filter(s => s.defaultChecked).map(s => s.type))
  );

  const handleToggle = (type: AddonSuggestionType, checked: boolean) => {
    const newSet = new Set(checkedTypes);
    if (checked) {
      newSet.add(type);
    } else {
      newSet.delete(type);
    }
    setCheckedTypes(newSet);
  };

  /**
   * CORE-BP item 3 — exactly one outcome per showing, and never zero.
   *
   * Every way out of this modal is an answer to the same question, so every way
   * out must record one. Escape, the backdrop and the X previously reached
   * `onClose` directly and recorded NOTHING, so the whole showing vanished --
   * the same loss the `dismissed_keys: []` defect caused, through a different
   * door.
   *
   * The flag stops the other half of that: an accept must not also count as a
   * dismissal on its way out. It is a ref rather than state because it guards a
   * decision that has already happened and must not wait for a re-render, and
   * it resets on each opening so a second showing is recorded like the first.
   */
  const reported = useRef(false);
  useEffect(() => {
    if (isOpen) reported.current = false;
  }, [isOpen]);

  const handleAccept = () => {
    const acceptedTypes = Array.from(checkedTypes);
    reported.current = true;
    onAccept(acceptedTypes);
    onClose();
  };

  const handleDismiss = () => {
    reported.current = true;
    onDismiss();
    onClose();
  };

  /** Escape, the backdrop and the X. Records the outcome; advances nothing. */
  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (!reported.current) {
      reported.current = true;
      onCloseWithoutChoice?.();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Add recommended services?</DialogTitle>
          <DialogDescription>
            We noticed jobs that typically require the items below for{' '}
            <span className="font-medium text-foreground">
              {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.type}
              className="flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={`addon-${suggestion.type}`}
                checked={checkedTypes.has(suggestion.type)}
                onCheckedChange={(checked) => handleToggle(suggestion.type, checked as boolean)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor={`addon-${suggestion.type}`}
                    className="font-semibold text-foreground cursor-pointer"
                  >
                    {suggestion.label}
                  </label>
                  <Badge variant="secondary" className="text-xs">
                    {suggestion.matchedTriggers.length} match{suggestion.matchedTriggers.length !== 1 ? 'es' : ''}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{suggestion.reason}</p>
                {suggestion.type === 'adas' && suggestion.evidence && suggestion.evidence.items.length > 0 && (
                  // OptiVehicle Phase 3A — explanation only. Reported equipment, not
                  // an OE procedure; it never pre-checks or adds anything.
                  <div className="rounded-md border border-border bg-background/60 p-2 text-xs space-y-1" data-testid="adas-advisory-evidence">
                    <p className="text-foreground break-words">
                      <span className="font-medium">Vehicle configuration reports:</span>{' '}
                      {suggestion.evidence.commonSource
                        ? suggestion.evidence.items.map((i) => i.abbr).join(', ')
                        : suggestion.evidence.items.map((i) => `${i.abbr} (${i.sourceLabel})`).join(', ')}
                    </p>
                    {suggestion.evidence.commonSource && (
                      <p className="text-muted-foreground">Source: {suggestion.evidence.commonSource}</p>
                    )}
                    <p className="text-muted-foreground">
                      ADAS equipment reported — verify the applicable OEM calibration requirements before estimating or performing calibration.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleDismiss}>
            {mode === 'existing-ticket' ? 'No thanks' : 'Create without'}
          </Button>
          <Button onClick={handleAccept} disabled={checkedTypes.size === 0}>
            {mode === 'existing-ticket'
              ? `Add selected (${checkedTypes.size})`
              : `Add selected & create (${checkedTypes.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
